import type { JSX } from 'preact';
import { VisPlaceholder } from './VisPlaceholder';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { Size } from '#utils/UNSAFE_size';

type Props = {
  /**
   * The text content for the status message.
   */
  text: string;

  /**
   * The width of the component. The default value for this is theme driven.
   */
  width?: Size;

  /**
   * The height of the component. The default value for this is theme driven.
   */
  height?: Size;

  /**
   * The aria label of the component.
   */
  'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];

  /**
   * One or more ids (separated by spaces) of elements that describe the component. This will be used for the aria-describedby of the DOM element.
   */
  'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];

  /**
   * One or more ids (separated by spaces) of elements that label the component. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
};

export function VisStatusMessage(props: Props) {
  const textId = getRandomId();
  const { ariaLabelledBy, id } = getAriaLabelledBy({
    ariaLabel: props['aria-label'],
    ariaLabelledby: props['aria-labelledby'],
    textId
  });
  return (
    <VisPlaceholder
      id={id}
      width={props.width}
      height={props.height}
      aria-label={props['aria-label']}
      aria-describedby={props['aria-describedby']}
      aria-labelledby={ariaLabelledBy}>
      <span id={textId}>{props.text}</span>
    </VisPlaceholder>
  );
}

const getAriaLabelledBy = (props: {
  ariaLabel?: string;
  ariaLabelledby?: string;
  textId: string;
}): { ariaLabelledBy: string; id?: string } => {
  const id = props.ariaLabel ? getRandomId() : undefined;
  const ariaLabelledBy = [props.ariaLabelledby, id, props.textId].filter(Boolean).join(' ');
  return { ariaLabelledBy, id };
};
